'use client'

import React from 'react'
import { BottomNavigation, BottomNavigationAction, Box, Paper } from '@mui/material'

import NewBadge from '@/src/components/common/home/flightSearch/searchForm/NewBadge'
import HolidaysSpotlightWrapper from '@/src/components/common/holidays/holidaysSpotlight'
import { HighlightRing, badgeRevealSx } from '@/src/components/common/ui/highlightRing'
import { useLocale } from 'next-intl'

import { ToolbarLink } from '@/src/types/bottomToolbar'

/** A toolbar link with its label already translated, plus an optional translated badge. */
type TranslatedToolbarLink = ToolbarLink & { badge?: string }

interface BottomToolbarProps {
    currentValue: string
    toolbarLinks: TranslatedToolbarLink[]
    onNavigationChange: (value: string) => void
    topSlot?: React.ReactNode
}

const BottomToolbar: React.FC<BottomToolbarProps> = ({ currentValue, toolbarLinks, onNavigationChange, topSlot }) => {
    const locale = useLocale()
    const isRTL = locale === 'ar'

    const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
        onNavigationChange(newValue)
    }

    return (
        <Paper
            sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1000,
                overflow: 'visible'
            }}
            elevation={3}
        >
            {topSlot}
            <BottomNavigation
                value={currentValue}
                onChange={handleChange}
                showLabels
                sx={{
                    px: 2,
                    py: 1.5,
                    // The bubble is drawn above the bar; neither the bar nor its row may clip it.
                    overflow: 'visible',
                    direction: isRTL ? 'rtl' : 'ltr',
                    height: 'auto',
                    minHeight: 56
                }}
            >
                {toolbarLinks.map(link => {
                    // The ring and spotlight point at a tab the reader has not opened; on the
                    // page it already leads to they only cover the page's own copy.
                    const isFlagged = Boolean(link.badge) && link.value !== currentValue

                    return (
                        <BottomNavigationAction
                            key={link.value}
                        label={
                            isFlagged ? (
                                // The ring rings the word, not the whole item: around the action
                                // it swallowed the icon and crossed its neighbours.
                                <Box
                                    component="span"
                                    sx={{
                                        position: 'relative',
                                        display: 'inline-flex',
                                        px: 1,
                                        py: '2px'
                                    }}
                                >
                                    {link.label}
                                    <HighlightRing />
                                    <HolidaysSpotlightWrapper caret="down" />
                                </Box>
                            ) : (
                                link.label
                            )
                        }
                        value={link.value}
                        icon={
                            link.badge ? (
                                // Pinned to the icon, not appended to the label: five labels have
                                // to share the width, and a sixth word pushes the last one off.
                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                    {link.icon}
                                    <Box
                                        component="span"
                                        sx={{
                                            ...badgeRevealSx,
                                            position: 'absolute',
                                            top: -6,
                                            insetInlineStart: '55%'
                                        }}
                                    >
                                        <NewBadge
                                            label={link.badge}
                                            sx={{ fontSize: '0.5rem', px: '3px', py: '1px', borderRadius: '3px' }}
                                        />
                                    </Box>
                                </Box>
                            ) : (
                                link.icon
                            )
                        }
                        sx={{
                            minWidth: 'auto',
                            px: { xs: 1, sm: 2 },
                            position: 'relative',
                            overflow: 'visible',
                            zIndex: 1,
                            '& .MuiBottomNavigationAction-label': {
                                mt: isRTL ? 0 : 0.5,
                                fontWeight: 600,
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                textAlign: 'center',
                                whiteSpace: 'nowrap', // Prevent text wrapping
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100%',
                                lineHeight: 1.2
                            },
                            '& .MuiBottomNavigationAction-icon': {
                                mb: 0.5
                            }
                        }}
                        />
                    )
                })}
            </BottomNavigation>
        </Paper>
    )
}

export { BottomToolbar }
export type { BottomToolbarProps }
